import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  listMyPayments,
  resendMyPaymentReceipt,
  listMyPaymentDeliveryStatus,
  getMyPaymentDetail,
} from "@/lib/api/portal.functions";
import { resendInvoiceEmail } from "@/lib/api/invoice-resend.functions";
import {
  Download,
  Eye,
  Mail,
  RefreshCcw,
  CreditCard,
  Loader2,
  Search,
  Info,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/my-payments")({
  component: MyPaymentsPage,
});

const SITE = "https://nkadimengdannylcs.com";

function statusBadge(s: string) {
  const norm = (s || "").toLowerCase();
  if (norm === "paid")
    return (
      <Badge className="bg-green-600 hover:bg-green-700">Paid via PayFast</Badge>
    );
  if (norm === "failed" || norm === "cancelled")
    return <Badge variant="destructive">{s}</Badge>;
  if (norm === "processing")
    return <Badge className="bg-blue-600 hover:bg-blue-700">Processing</Badge>;
  if (norm === "refunded")
    return <Badge className="bg-orange-600 hover:bg-orange-700">Refunded</Badge>;
  return <Badge variant="secondary">{s || "pending"}</Badge>;
}

function deliveryBadge(
  doc: "invoice" | "receipt",
  info?: {
    status?: string;
    at?: string;
    error?: string | null;
    retries?: number;
  },
) {
  if (!info)
    return (
      <Badge variant="outline" className="text-[10px]">
        {doc}: queued
      </Badge>
    );
  const s = (info.status || "").toLowerCase();
  let className = "bg-slate-500 text-white";
  let label: string = info.status ?? "";
  if (s === "sent") {
    className = "bg-emerald-600 text-white";
    label = "sent";
  } else if (s === "pending") {
    className = "bg-amber-500 text-white";
    label = "queued";
  } else if (s === "retrying") {
    className = "bg-amber-600 text-white";
    label = `retrying${info.retries ? ` (${info.retries})` : ""}`;
  } else if (s === "failed" || s === "dlq") {
    className = "bg-red-600 text-white";
    label = "failed";
  } else if (s === "bounced" || s === "complained" || s === "suppressed") {
    className = "bg-red-700 text-white";
    label = s;
  }
  return (
    <Badge
      className={`${className} text-[10px]`}
      title={info.error || info.at || ""}
    >
      {doc}: {label}
    </Badge>
  );
}

function fmtDate(d?: string | null) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-ZA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return d;
  }
}

function kindLabel(k: string) {
  if (k === "credit") return "Credit top-up";
  if (k === "contract") return "Contract / document";
  if (k === "cv") return "AI CV generation";
  return k;
}

function MyPaymentsPage() {
  const fetchPayments = useServerFn(listMyPayments);
  const fetchDelivery = useServerFn(listMyPaymentDeliveryStatus);
  const fetchDetail = useServerFn(getMyPaymentDetail);
  const resendInvoice = useServerFn(resendInvoiceEmail);
  const resendReceipt = useServerFn(resendMyPaymentReceipt);
  const [busy, setBusy] = useState<string | null>(null);
  const [viewerEmail, setViewerEmail] = useState<string>("");

  useMemo(() => {
    // one-shot fetch of the current user's email for signed receipt URLs
    import("@/integrations/supabase/client").then(({ supabase }) => {
      supabase.auth.getUser().then((r) => {
        setViewerEmail(r.data.user?.email ?? "");
      });
    });
  }, []);

  const [kindFilter, setKindFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [detailRef, setDetailRef] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["my-payments"],
    queryFn: () => fetchPayments({}),
  });


  const payments = (data?.payments ?? []) as any[];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payments.filter((p) => {
      if (kindFilter !== "all" && p.kind !== kindFilter) return false;
      if (
        statusFilter !== "all" &&
        (p.status || "").toLowerCase() !== statusFilter
      )
        return false;
      if (q) {
        const hay =
          `${p.reference} ${p.title} ${p.pf_payment_id ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [payments, kindFilter, statusFilter, search]);

  const { data: deliveryData, refetch: refetchDelivery } = useQuery({
    queryKey: [
      "my-payments-delivery",
      payments.map((p: any) => p.reference).join(","),
    ],
    queryFn: () =>
      fetchDelivery({
        data: { references: payments.map((p: any) => p.reference) },
      }),
    enabled: payments.length > 0,
    refetchInterval: 15_000,
  });
  const statuses: Record<string, any> = (deliveryData as any)?.statuses ?? {};

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ["my-payment-detail", detailRef],
    queryFn: () => fetchDetail({ data: { reference: detailRef! } }),
    enabled: !!detailRef,
  });

  async function handleResendInvoice(reference: string) {
    setBusy(`inv:${reference}`);
    try {
      const r: any = await resendInvoice({ data: { reference } });
      if (r?.ok) toast.success(`Invoice re-sent to ${r.sentTo}`);
      else toast.error(r?.message || "Could not resend invoice");
    } catch (e: any) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(null);
      refetchDelivery();
    }
  }

  async function handleResendReceipt(reference: string) {
    setBusy(`rec:${reference}`);
    try {
      const r: any = await resendReceipt({ data: { reference } });
      if (r?.ok) toast.success(`Receipt re-sent to ${r.email}`);
      else if (r?.queued)
        toast.info("Email queued for retry — we'll keep trying.");
      else toast.error(r?.reason || "Could not resend receipt");
    } catch (e: any) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(null);
      refetchDelivery();
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <CreditCard className="h-6 w-6" /> My Payments
          </h1>
          <p className="text-sm text-muted-foreground">
            Every invoice and receipt for your purchases. Invoices and receipts
            are generated and emailed automatically — you can also view,
            download or re-send them here.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </header>

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle>Recent transactions</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search reference, PF id or item…"
                className="h-8 w-64 pl-7 text-xs"
              />
            </div>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="h-8 w-[160px] text-xs">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="cv">AI CV</SelectItem>
                <SelectItem value="credit">Credit top-up</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-[160px] text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              {filtered.length} of {payments.length}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your payments…
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No payments match the current filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p: any) => {
                    const paid = (p.status || "").toLowerCase() === "paid";
                    const tokenQs = p.public_token || p.download_token
                      ? `&t=${encodeURIComponent(p.public_token || p.download_token)}`
                      : "";
                    const invoiceUrl = `${SITE}/api/public/receipt?ref=${encodeURIComponent(
                      p.reference,
                    )}${tokenQs}&doc=invoice`;
                    const receiptUrl = `${SITE}/api/public/receipt?ref=${encodeURIComponent(
                      p.reference,
                    )}${tokenQs}`;


                    const statusUrl = `/payment-status/${encodeURIComponent(
                      p.reference,
                    )}`;
                    const invBusy = busy === `inv:${p.reference}`;
                    const recBusy = busy === `rec:${p.reference}`;
                    return (
                      <TableRow key={`${p.kind}:${p.reference}`}>
                        <TableCell className="text-xs text-muted-foreground">
                          <div>{fmtDate(p.created_at)}</div>
                          {p.paid_at ? (
                            <div className="text-green-700">
                              Paid {fmtDate(p.paid_at)}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <button
                            type="button"
                            className="underline-offset-2 hover:underline"
                            onClick={() => setDetailRef(p.reference)}
                          >
                            {p.reference}
                          </button>
                          {p.invoice_reference &&
                          p.invoice_reference !== p.reference ? (
                            <div className="text-[10px] text-muted-foreground">
                              Invoice ref:{" "}
                              <span className="font-mono">
                                {p.invoice_reference}
                              </span>
                            </div>
                          ) : null}
                          {p.pf_payment_id ? (
                            <div className="text-[10px] text-muted-foreground">
                              PF #{p.pf_payment_id}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="max-w-[260px]">
                          <div className="text-sm font-medium">{p.title}</div>
                          <div className="text-[11px] uppercase text-muted-foreground">
                            {kindLabel(p.kind)}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap font-medium">
                          R{p.amount_zar.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {statusBadge(p.status)}
                            <div className="flex flex-wrap gap-1">
                              {deliveryBadge(
                                "invoice",
                                statuses[p.reference]?.invoice,
                              )}
                              {paid
                                ? deliveryBadge(
                                    "receipt",
                                    statuses[p.reference]?.receipt,
                                  )
                                : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDetailRef(p.reference)}
                              title="View ITN & email log"
                            >
                              <Info className="h-3.5 w-3.5" />
                            </Button>
                            <Button asChild size="sm" variant="outline">
                              <a
                                href={invoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Eye className="mr-1 h-3.5 w-3.5" /> Invoice
                              </a>
                            </Button>
                            <Button asChild size="sm" variant="outline">
                              <a href={invoiceUrl} download>
                                <Download className="mr-1 h-3.5 w-3.5" /> PDF
                              </a>
                            </Button>
                            {paid ? (
                              <>
                                <Button asChild size="sm" variant="outline">
                                  <a
                                    href={receiptUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <Eye className="mr-1 h-3.5 w-3.5" /> Receipt
                                  </a>
                                </Button>
                                <Button asChild size="sm" variant="outline">
                                  <a href={receiptUrl} download>
                                    <Download className="mr-1 h-3.5 w-3.5" />{" "}
                                    PDF
                                  </a>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={recBusy}
                                  onClick={() =>
                                    handleResendReceipt(p.reference)
                                  }
                                >
                                  {recBusy ? (
                                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Mail className="mr-1 h-3.5 w-3.5" />
                                  )}
                                  Resend receipt
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button asChild size="sm">
                                  <Link to={statusUrl}>Pay / track</Link>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={invBusy}
                                  onClick={() =>
                                    handleResendInvoice(p.reference)
                                  }
                                >
                                  {invBusy ? (
                                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Mail className="mr-1 h-3.5 w-3.5" />
                                  )}
                                  Resend invoice
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!detailRef}
        onOpenChange={(o) => (!o ? setDetailRef(null) : null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">{detailRef}</DialogTitle>
            <DialogDescription>
              PayFast ITN events, processing timestamps, and email delivery
              history for this order.
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center gap-2 py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-5">
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                  PayFast ITN events
                </h3>
                {(detailData as any)?.events?.length ? (
                  <ul className="space-y-1 text-xs">
                    {(detailData as any).events.map((e: any) => (
                      <li
                        key={e.id}
                        className="flex flex-wrap items-center gap-2 rounded border bg-muted/40 px-2 py-1"
                      >
                        <Badge
                          variant={e.processed ? "default" : "destructive"}
                          className="text-[10px]"
                        >
                          {e.event_type}
                        </Badge>
                        <span className="text-muted-foreground">
                          {fmtDate(e.created_at)}
                        </span>
                        {e.processed_at ? (
                          <span className="text-emerald-700">
                            processed {fmtDate(e.processed_at)}
                          </span>
                        ) : null}
                        {e.error_message ? (
                          <span className="text-red-700">
                            {e.error_message}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No PayFast webhook events recorded yet.
                  </p>
                )}
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                  Email delivery
                </h3>
                {(detailData as any)?.emails?.length ? (
                  <ul className="space-y-1 text-xs">
                    {(detailData as any).emails.map((m: any, i: number) => (
                      <li
                        key={i}
                        className="flex flex-wrap items-center gap-2 rounded border bg-muted/40 px-2 py-1"
                      >
                        <Badge variant="outline" className="text-[10px]">
                          {m.template_name}
                        </Badge>
                        <span>{m.recipient_email}</span>
                        <Badge
                          className={`text-[10px] ${
                            m.status === "sent"
                              ? "bg-emerald-600"
                              : m.status === "failed"
                                ? "bg-red-600"
                                : "bg-slate-500"
                          } text-white`}
                        >
                          {m.status}
                        </Badge>
                        <span className="text-muted-foreground">
                          {fmtDate(m.created_at)}
                        </span>
                        {m.error_message ? (
                          <span className="text-red-700">
                            {m.error_message}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No emails have been sent for this reference yet.
                  </p>
                )}
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                  Retry queue
                </h3>
                {(detailData as any)?.jobs?.length ? (
                  <ul className="space-y-1 text-xs">
                    {(detailData as any).jobs.map((j: any) => (
                      <li
                        key={j.id}
                        className="flex flex-wrap items-center gap-2 rounded border bg-muted/40 px-2 py-1"
                      >
                        <Badge variant="outline" className="text-[10px]">
                          {j.status}
                        </Badge>
                        <span>{j.attempts} attempts</span>
                        {j.next_retry_at ? (
                          <span className="text-muted-foreground">
                            next {fmtDate(j.next_retry_at)}
                          </span>
                        ) : null}
                        {j.last_error ? (
                          <span className="text-red-700">{j.last_error}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No retries queued.
                  </p>
                )}
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <p className="text-xs text-muted-foreground">
        Invoices and receipts are generated automatically right after each
        successful payment and emailed to you in the background. If an email is
        delayed, your purchase still unlocks immediately and you can re-send the
        document from this page at any time.
      </p>
    </div>
  );
}
